import React, { useEffect, useState } from "react";
import { Typography } from "@material-tailwind/react";
import {
    EyeIcon,
} from "@heroicons/react/24/solid";
import { fetchOrderPayments } from "@/store/slices/orderSlice";
import { useDispatch, useSelector } from "react-redux";
import DownloadFile from "@/components/DownloadFile";
import DocumentViewModal from "@/components/DocumentViewModal";
import { useTranslation } from "react-i18next";

function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return (
        d.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
        }) +
        " | " +
        d.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        })
    );
}

export function OrderPayments({ orderId, rfq_id }) {
    const { t } = useTranslation('order');
    const dispatch = useDispatch();
    const [filter, setFilter] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { order_payments, loading } = useSelector((state) => state.order);

    useEffect(() => {
        if (rfq_id) dispatch(fetchOrderPayments({ order_id: rfq_id, filter: filter }));
    }, [rfq_id, dispatch, filter]);


    return (
        <div className="w-full">
            <div className="mb-7 grid gap-y-3 gap-x-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="border border-blue-gray-100 shadow-sm p-4 rounded-xl bg-white">
                    <Typography variant="h4" color="blue-gray">
                        {(order_payments?.summary?.total_amount ?? 0).toLocaleString("en-IN")}
                    </Typography>
                    <Typography variant="small" className="font-normal text-blue-gray-600 mt-2">
                        {t('total_amount_label')} ({order_payments?.summary?.currency_code})
                    </Typography>
                </div>
                <div className="border border-blue-gray-100 shadow-sm p-4 rounded-xl bg-white">
                    <Typography variant="h4" color="blue-gray">
                        {(order_payments?.summary?.amount_paid ?? 0).toLocaleString("en-IN")}
                    </Typography>
                    <Typography variant="small" className="font-normal text-blue-gray-600 mt-2">
                        {t('amount_paid_label')} ({order_payments?.summary?.currency_code})
                    </Typography>
                </div>
                <div className="border border-blue-gray-100 shadow-sm p-4 rounded-xl bg-white">
                    <Typography variant="h4" color="blue-gray">
                        {(order_payments?.summary?.amount_pending ?? 0).toLocaleString("en-IN")}
                    </Typography>
                    <Typography variant="small" className="font-normal text-blue-gray-600 mt-2">
                        {t('amount_pending_label')} ({order_payments?.summary?.currency_code})
                    </Typography>
                </div>
                <div className="border border-blue-gray-100 shadow-sm p-4 rounded-xl bg-white">
                    <Typography variant="h4" color="blue-gray">
                        {(order_payments?.summary?.refund_request ?? 0).toLocaleString("en-IN")}
                    </Typography>
                    <Typography variant="small" className="font-normal text-blue-gray-600 mt-2">
                        {t('refund_request_label')}  ({order_payments?.summary?.currency_code})
                    </Typography>
                </div>
            </div>
            {/* Controls Bar - Order Items (adapted for Payments) */}
            <div className="w-full flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                <div className="flex items-center gap-4">
                    <Typography variant="h6" color="blue-gray" className="font-bold">
                        {t('invoices_title', { count: order_payments?.payments?.length ?? 0 })}
                    </Typography>
                </div>

                
            </div>

            <div className="w-full overflow-x-auto rounded-lg">
                <table className="w-full min-w-[800px]">
                    <tbody>
                        {!(order_payments?.payments?.length) ? (
                            <tr>
                                <td colSpan="5" className="py-10 text-center text-gray-500 italic">
                                    {t('no_invoices_message')}
                                </td>
                            </tr>
                        ) : (
                            order_payments.payments.map((item, index) => (<>
                                <tr key={item.id ?? index} className="border-b border-gray-300 hover:bg-gray-50 transition">
                                    {/* Invoice Name */}
                                    <td className="py-4 px-4 first:pl-0 last:pr-0 align-top">
                                        <span className="text-sm font-medium text-gray-900">{t('invoice_number_label', { number: index + 1 })}</span>
                                    </td>

                                      {/* Amount */}
                                    <td className="px-4 py-4 whitespace-nowrap">
                                        <span className="text-sm font-bold text-gray-900">
                                            {item?.currency_code || ''}  {item.amount_paid && Number(item.amount_paid).toLocaleString() || "N/A"}
                                        </span>
                                    </td>

                                    {/* Status Badge */}
                                    <td className="px-4 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                            ${item.status === "pending" ? "bg-yellow-100 text-yellow-800" : ""}
                                            ${item.status === "approved" ? "bg-green-100 text-green-800" : ""}
                                            ${item.status === "rejected" ? "bg-red-100 text-red-800" : ""}
                                        `}>
                                            {t(`status.${item?.status}`)}
                                        </span>
                                    </td>

                                    <td className="px-4 py-4 whitespace-nowrap">
                                        <span className="text-sm font-medium text-gray-900">{item?.payment_mode}</span>
                                    </td>

                                    {/* Date */}
                                    <td className="px-4 py-4 whitespace-nowrap">
                                        <span className="text-sm text-gray-500">{formatDate(item.created_at)}</span>
                                    </td>

                                    {/* Actions */}
                                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end gap-2">
                                            {/* {item?.payment_invoice && (
                                                <EyeIcon title="Invoice" className="h-4 w-4 text-gray-500 cursor-pointer" onClick={() => setIsModalOpen(item?.payment_invoice)}/>
                                            )} */}
                                            {item?.payment_invoice && (
                                                <DownloadFile fileUrl={item.payment_invoice} label={t('invoice_label')}/>
                                            )}
                                            {item?.payment_receipt && (
                                                <EyeIcon title={t('customer_payment_receipt_title')} className="h-4 w-4 text-gray-500 cursor-pointer" onClick={() => setIsModalOpen(item?.payment_receipt[0])}/>
                                            )}
                                            
                                        </div>
                                    </td>
                                </tr>
                                {item?.rejection_reason &&
                                 <tr key={item.id+"rejection_reason"} className="border-b border-gray-300 hover:bg-gray-50 transition">
                                    {/* Invoice Name */}
                                    <td className="py-4 px-4 first:pl-0 last:pr-0 align-top" colSpan={5}>
                                        <span className="text-sm font-medium text-gray-900 text-red-800">{t('rejection_reason_label')}: {item?.rejection_reason}</span>
                                    </td>
                                </tr>
                                }</>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {isModalOpen && <DocumentViewModal documentUrl={isModalOpen} setIsDocumentViewModalOpen={setIsModalOpen} />}
            
        </div>
    );
}

export default OrderPayments;
